import type { FromBaseRow as Row } from './from-base-context';
import { FromBaseInputMissing, FromBaseStageMissing, fromBaseRecord, fromBaseUuid, fromBaseCatalogWithSources } from './from-base-context';
import type { FromBasePlayerResourceMetadata } from './from-base-player-resources';
import type { FromBaseInventoryMetadata } from './from-base-inventory';
import { compileNpcStagesEntryFromBase, compileNpcSavedInventoryFromBase, createFromBaseNpcGrantContext } from './from-base-npc-stages';
import { readInventoryField } from './inventory-raw-readers';
import { readItemScalarLayerSlot } from './layer-cell-readers';
import { createCreatureEquippedCountsCompiler } from './creature-equipped-counts';
import { opportunityAttackInput } from './opportunity-attack-input';
import { readItemField } from './item-field-reader';
import { hydrateInventoryDefinitionMeshes } from './from-base-inventory-assets';
import { withActorAvatarBodies } from './from-base-avatar-body';
import { createFromBaseDefinitionFinalizer, fromBaseCatalogFinalizationGaps } from './from-base-definition-finalization';
import type { FromBaseDefinitionCache } from './from-base-definition-finalization';
import { constructNpcPostFromBase } from './from-base-npc-post';
import { constructNpcCastableFromBase } from './from-base-npc-castable';
import { readAbilityEntryId } from './inventory-ability-grant-helpers';

/** Shared definition factory also completes creature providers reached while
 * expanding a non-creature definition's reference graph. */
export function createNpcDefinitionFinalizer(base: Row, metadata: FromBasePlayerResourceMetadata, inventoryMetadata: FromBaseInventoryMetadata,
    cache?: FromBaseDefinitionCache) {
    base = { ...base, catalog: fromBaseCatalogWithSources(fromBaseRecord(base.catalog) ? base.catalog : {}, metadata.stageSources) };
    const closure = Object.fromEntries(['catalog', 'missing_definition_ids', 'enchantment_templates',
        'enchantment_template_enchantments', 'missing_enchantment_template_ids', 'charges', 'enchantment_match_candidates',
        'enchantment_match_rows', 'random_table_rows', 'definition_videos']
        .filter(key => Object.prototype.hasOwnProperty.call(base, key)).map(key => [key, base[key]]));
    let saved: Row[] | undefined;
    const savedInventory = () => saved ??= compileNpcSavedInventoryFromBase(base, inventoryMetadata);
    const inventoryLookup = (id: string): Row | null => {
        const visit = (rows: readonly Row[]): Row | null => {
            for (const row of rows) {
                if (String(readInventoryField(row, 'inventory_id')).toUpperCase() === id.toUpperCase()) return row;
                if (Array.isArray(row.inventory_in_container)) { const found = visit(row.inventory_in_container as Row[]); if (found) return found; }
            }
            return null;
        };
        const row = visit(savedInventory());
        if (!row) throw new FromBaseInputMissing('saved-inventory-instance', id);
        return row;
    };
    const runtime = { ...metadata, inventoryMetadata, postReference: false, staticInventoryMemo: { key: '', nodes: [] as Row[] },
        ...(fromBaseRecord(base.encounter_creatures) ? { savedInventory, inventoryLookup } : {}),
        definitionLookup: (id: string) => finalize.definition(id, []) };
    const actorHost = (host: Row): boolean => fromBaseRecord(host.encounter_creatures)
        && fromBaseRecord(base.encounter_creatures)
        && fromBaseUuid(readItemField(host.encounter_creatures, 'encounter_creature_id'))
        && readItemField(host.encounter_creatures, 'encounter_creature_id') === readItemField(base.encounter_creatures, 'encounter_creature_id');
    const finalize = createFromBaseDefinitionFinalizer(base, inventoryMetadata, raw => {
        const child = compileNpcStagesEntryFromBase({ ...closure, ...raw }, runtime, inventoryMetadata);
        if (!child.row || child.missingStages[0] !== 'catalog-lean-normalization')
            throw new FromBaseStageMissing(child.missingStages[0]!, child.diagnostics.join(';'));
        return child.row;
    }, (host, nested) => {
        // A standalone form definition also has nested=false. Only the actual actor owns its baked avatar.
        const actor = !nested && actorHost(host);
        const row = constructNpcPostFromBase(actor ? base : closure, { ...runtime, postReference: true }, host);
        const meshes = actor ? readItemField({ meshes: [base.meshes ?? []] }, 'meshes') : [];
        if (Array.isArray(meshes) && meshes.length) {
            if (!inventoryMetadata.definitionAssets) throw new FromBaseInputMissing('actor-mesh-metadata', String(base.entity_id));
            const hydrated = withActorAvatarBodies(hydrateInventoryDefinitionMeshes(meshes, inventoryMetadata.definitionAssets,
                inventoryMetadata.sizeCatalog.map(size => String(readItemField(size, 'item_id')))), meshes,
                base.encounter_creatures, readItemField(base, 'avatar_wardrobe'));
            const assets = fromBaseRecord(row.assets) ? row.assets : {};
            const held = readItemField({ meshes: [assets['3d'] ?? []] }, 'meshes');
            const ids = new Set(hydrated.map(mesh => readItemField(mesh, 'id')));
            if (hydrated.length) row.assets = { ...assets, '3d': [...hydrated,
                ...(Array.isArray(held) ? held.filter(mesh => !ids.has(readItemField(mesh, 'id'))) : [])] };
        }
        return row;
    }, (host, nested) => {
        if (!nested) {
            if (fromBaseRecord(host.encounter_creatures) && fromBaseUuid(readItemField(host.encounter_creatures, 'encounter_creature_id'))) {
                const context = createFromBaseNpcGrantContext(base, runtime, host, 'catalog-lean-normalization');
                const oa = opportunityAttackInput(host, { itemField: context.readers.itemField, layerValue: context.readers.layerValue,
                    layerEntries: (row, key) => Object.values(context.readers.layerEntries(row, key)),
                    scalarSlot: readItemScalarLayerSlot,
                    entryUuid: entry => readAbilityEntryId(entry, { itemField: context.readers.itemField, resolveCarrierRef: context.definition }),
                    definition: context.definition,
                    equippedCounts: row => createCreatureEquippedCountsCompiler({ ...context.deps, ...runtime.equippedCounts })(row) as Row }, false);
                if ('unknown' in oa) throw new FromBaseStageMissing('catalog-lean-normalization', `opportunity-input:${oa.unknown}`);
                ((host.computed as Row).creature as Row).opportunity_attack = oa.packet;
            }
            constructNpcCastableFromBase(actorHost(host) ? base : closure, runtime, host);
        }
        return host;
    }, cache);
    return { ...finalize, metadata: runtime };
}

export function compileNpcFinalizationEntryFromBase(base: Row, metadata: FromBasePlayerResourceMetadata, inventoryMetadata: FromBaseInventoryMetadata,
    cache?: FromBaseDefinitionCache) {
    const finalize = createNpcDefinitionFinalizer(base, metadata, inventoryMetadata, cache);
    const result = compileNpcStagesEntryFromBase(base, finalize.metadata, inventoryMetadata, true);
    if (!result.row || result.missingStages[0] !== 'catalog-lean-normalization') return result;
    try {
        result.row = finalize.finalize(result.row);
        const gaps = fromBaseCatalogFinalizationGaps(base, inventoryMetadata, result.row);
        if (gaps.length) result.diagnostics.push(...gaps);
        else { result.completedStages.push('catalog-lean-normalization'); result.missingStages.shift(); }
    } catch (error) {
        if (!(error instanceof FromBaseInputMissing || error instanceof FromBaseStageMissing)) throw error;
        result.diagnostics.push(error.message);
    }
    return result;
}
